import React, { useMemo } from 'react'
import { Card, Typography } from '@material-ui/core'
import styled from 'styled-components'
import moment from 'moment'
import { useNavigate, useLocation } from 'react-router'

// ============================================================================
// Types & Interfaces
// ============================================================================

interface FinancialsData {
  unpaidCount: number
  unpaidAmount: number
}

interface CategoryChartData {
  month: string
  categories: {
    dc?: number
    cc?: number
    billing?: number
    done?: number
  }
}

interface Props {
  financials: FinancialsData
  categoryChart: CategoryChartData[]
}

interface CategoryConfig {
  key: keyof CategoryChartData['categories']
  label: string
  color: string
}

// ============================================================================
// Constants
// ============================================================================

// Chart color palette matching ContainerCharts.tsx stagesPieColorsPalette - using lighter shades
const CATEGORY_CONFIGS: CategoryConfig[] = [
  { key: 'dc', label: 'DC', color: '#fb9a99' }, // Light red/pink from chart palette
  { key: 'cc', label: 'CC', color: '#a6cee3' }, // Light blue from chart palette
  { key: 'billing', label: 'Billing', color: '#b2df8a' }, // Light green from chart palette
  { key: 'done', label: 'Done', color: '#fdbf6f' }, // Light orange/tan from chart palette
]

const MOBILE_BREAKPOINT = 768
const MOBILE_SMALL_BREAKPOINT = 480
const CURRENCY_LOCALE = 'en-US'
const CURRENCY_DECIMAL_PLACES = 2

// Static month names - moved outside to avoid recreating on every render
const MONTH_NAMES = moment.months()

// Currency formatter - created once and reused
const CURRENCY_FORMATTER = new Intl.NumberFormat(CURRENCY_LOCALE, {
  minimumFractionDigits: CURRENCY_DECIMAL_PLACES,
  maximumFractionDigits: CURRENCY_DECIMAL_PLACES,
})

// ============================================================================
// Styled Components
// ============================================================================

const WidgetsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  margin-top: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-top: 12px;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    gap: 8px;
    margin-top: 8px;
  }
`

const BaseCard = styled(Card)`
  padding: 16px;
  border: 1px solid ${(props) => props.theme.palette.divider} !important;
  box-shadow: none !important;
  font-family: ${(props) => props.theme.typography.fontFamily};
  display: flex;
  flex-direction: column;

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    padding: 12px;
  }
`

const ClickableCard = styled(BaseCard)`
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.palette.action.hover};
    box-shadow: ${(props) => props.theme.shadows[2]} !important;
  }

  &:active {
    background-color: ${(props) => props.theme.palette.action.selected};
  }
`

const CategoryChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 10px;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    gap: 8px;
  }
`

const ChartRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 50px;
  align-items: center;
  gap: 12px;
  min-height: 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 100px 1fr 40px;
    gap: 8px;
    min-height: 28px;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    grid-template-columns: 80px 1fr 35px;
    gap: 6px;
    min-height: 24px;
  }
`

const MonthLabel = styled.div`
  width: 120px;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.theme.palette.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100px;
    font-size: 0.75rem;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    width: 80px;
    font-size: 0.7rem;
  }
`

const BarContainer = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  height: 28px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  overflow: hidden;
  background-color: ${(props) => props.theme.palette.action.hover};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 24px;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    height: 20px;
  }
`

const BarSegment = styled.div<{ $color: string; $width: number }>`
  background-color: ${(props) => props.$color};
  width: ${(props) => props.$width}%;
  height: 100%;
  transition: width 0.3s ease;
  min-width: ${(props) => (props.$width > 0 ? '2px' : '0')};
  position: relative;

  &:not(:last-child) {
    border-right: 1px solid rgba(255, 255, 255, 0.2);
  }
`

const TotalLabel = styled.div`
  width: 50px;
  text-align: right;
  font-weight: 700;
  font-size: 0.875rem;
  color: ${(props) => props.theme.palette.text.primary};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 40px;
    font-size: 0.75rem;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    width: 35px;
    font-size: 0.7rem;
  }
`

const LegendContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => props.theme.palette.divider};
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 16px;
    margin-top: 16px;
    padding-top: 12px;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    gap: 12px;
    margin-top: 12px;
    padding-top: 8px;
  }
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => props.theme.palette.text.secondary};

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    font-size: 0.75rem;
    gap: 4px;
  }
`

const LegendColor = styled.div<{ $color: string }>`
  width: 18px;
  height: 18px;
  background-color: ${(props) => props.$color};
  border-radius: 3px;
  flex-shrink: 0;

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    width: 16px;
    height: 16px;
  }
`

const AmountText = styled(Typography)`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.palette.primary.main};
  margin-top: 8px;
  line-height: 1.2;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1.75rem;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    font-size: 1.5rem;
    margin-top: 6px;
  }
`

const SectionTitle = styled(Typography).attrs({ variant: 'h6' })`
  margin-bottom: 4px;
  font-weight: 600;

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    font-size: 1rem;
  }
`

const SectionSubtitle = styled(Typography).attrs({ variant: 'body2', color: 'textSecondary' })`
  margin-top: 4px;
  font-weight: 500;

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    font-size: 0.75rem;
  }
`

const CountText = styled(Typography).attrs({ variant: 'body1' })`
  margin-top: 8px;
  font-weight: 500;
  color: ${(props) => props.theme.palette.text.secondary};

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    font-size: 0.875rem;
    margin-top: 6px;
  }
`

const ChartTitle = styled(Typography).attrs({ variant: 'h6' })`
  margin-bottom: 20px;
  font-weight: 600;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-bottom: 16px;
  }

  @media (max-width: ${MOBILE_SMALL_BREAKPOINT}px) {
    font-size: 1rem;
    margin-bottom: 12px;
  }
`

// ============================================================================
// Helper Functions
// ============================================================================

const calculateTotal = (categories: CategoryChartData['categories']): number =>
  (categories.dc || 0) + (categories.cc || 0) + (categories.billing || 0) + (categories.done || 0)

const formatMonthLabel = (monthString: string): string => {
  const [year, month] = monthString.split('-')
  const monthIndex = parseInt(month, 10) - 1
  return `${MONTH_NAMES[monthIndex]} ${year}`
}

const formatCurrency = (amount: number): string => CURRENCY_FORMATTER.format(amount)

const calculateMaxValue = (categoryChart: CategoryChartData[]): number => {
  let max = 1
  for (const item of categoryChart) {
    const total = calculateTotal(item.categories)
    if (total > max) max = total
  }
  return max
}

// ============================================================================
// Main Component
// ============================================================================

const CalendarWidgets: React.FC<Props> = ({ financials, categoryChart }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const workSpace = useMemo(() => location.pathname.split('/')[1], [location.pathname])
  const maxValue = useMemo(() => calculateMaxValue(categoryChart), [categoryChart])

  const chartRows = useMemo(() => {
    return categoryChart.map((item) => {
      const total = calculateTotal(item.categories)
      const segments = CATEGORY_CONFIGS.filter((config) => (item.categories[config.key] || 0) > 0).map((config) => {
        const value = item.categories[config.key] || 0
        return {
          key: config.key,
          label: config.label,
          color: config.color,
          value,
          width: (value / maxValue) * 100,
        }
      })

      return {
        month: item.month,
        monthLabel: formatMonthLabel(item.month),
        total,
        segments,
      }
    })
  }, [categoryChart, maxValue])

  const handleFinancialsClick = () => {
    navigate(`/${workSpace}/shipments?payment_date=Absent`)
  }

  return (
    <WidgetsContainer>
      <ClickableCard variant='outlined' onClick={handleFinancialsClick}>
        <SectionTitle>Financials</SectionTitle>
        <SectionSubtitle>Unpaid Shipments</SectionSubtitle>
        <CountText>Count: {financials.unpaidCount}</CountText>
        <AmountText>€{formatCurrency(financials.unpaidAmount)}</AmountText>
      </ClickableCard>

      <BaseCard variant='outlined'>
        <ChartTitle>Shipments by Category and Month</ChartTitle>
        <CategoryChartContainer>
          {chartRows.map((row) => (
            <ChartRow key={row.month}>
              <MonthLabel>{row.monthLabel}</MonthLabel>
              <BarContainer>
                {row.segments.map((segment) => (
                  <BarSegment key={segment.key} $color={segment.color} $width={segment.width} title={`${segment.label}: ${segment.value}`} />
                ))}
              </BarContainer>
              <TotalLabel>{row.total}</TotalLabel>
            </ChartRow>
          ))}
        </CategoryChartContainer>
        <LegendContainer>
          {CATEGORY_CONFIGS.map((config) => (
            <LegendItem key={config.key}>
              <LegendColor $color={config.color} />
              <span>{config.label}</span>
            </LegendItem>
          ))}
        </LegendContainer>
      </BaseCard>
    </WidgetsContainer>
  )
}

export default CalendarWidgets
